// src/views/parent/PAJustifications.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useParentService } from "../../services/parentService";
import { urlimage } from "../../utils/config";
import Swal from "sweetalert2";

export default function PAJustifications({ slug }) {
  const navigate = useNavigate();
  const { enfantId } = useParams();
  const { getJustificationsEnfant, getEnfants } = useParentService();

  const [justifications, setJustifications] = useState([]);
  const [enfant, setEnfant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatut, setFilterStatut] = useState("");
  const [selectedJustification, setSelectedJustification] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageZoom, setImageZoom] = useState(1);
  const [imageLoadError, setImageLoadError] = useState(false);

  const hasFetchedRef = useRef(false);

  // ─── CHARGEMENT DES DONNÉES ─────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!slug || !enfantId) {
      setError("Paramètres manquants (parent ou élève).");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [enfantsData, justificationsData] = await Promise.all([
        getEnfants(slug),
        getJustificationsEnfant(slug, enfantId),
      ]);

      const enfantTrouve = (enfantsData || []).find((e) => e.id === parseInt(enfantId));
      setEnfant(enfantTrouve || null);
      setJustifications(justificationsData || []);
    } catch (err) {
      setError("Impossible de charger les justifications.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, enfantId]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    loadData();
  }, [loadData]);

  // Reset du flag si enfantId change (navigation vers un autre élève)
  useEffect(() => {
    hasFetchedRef.current = false;
  }, [enfantId]);

  // ─── HELPERS ─────────────────────────────────────────────────────────
  const getFullUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }
    return `${urlimage}${path}`;
  };

  const getMotifLabel = (motif) => {
    const labels = {
      maladie: "Maladie",
      famille: "Événement familial",
      transport: "Problème de transport",
      autre: "Autre",
    };
    return labels[motif] || motif;
  };

  const getMotifIcon = (motif) => {
    const icons = {
      maladie: "ti ti-thermometer",
      famille: "ti ti-users",
      transport: "ti ti-bus",
      autre: "ti ti-notes",
    };
    return icons[motif] || "ti ti-notes";
  };

  const getStatutColor = (statut) => {
    switch (statut) {
      case "approuvee": return "var(--gn)";
      case "rejetee": return "var(--rd)";
      default: return "var(--am)";
    }
  };

  const getStatutBg = (statut) => {
    switch (statut) {
      case "approuvee": return "rgba(59,109,17,0.1)";
      case "rejetee": return "rgba(220,53,69,0.1)";
      default: return "rgba(186,117,23,0.1)";
    }
  };

  const getStatutLabel = (statut) => {
    switch (statut) {
      case "approuvee": return "Approuvée";
      case "rejetee": return "Rejetée";
      default: return "En attente";
    }
  };

  const getStatutIcon = (statut) => {
    switch (statut) {
      case "approuvee": return "ti ti-check-circle";
      case "rejetee": return "ti ti-x-circle";
      default: return "ti ti-hourglass";
    }
  };

  const getFilterIcon = (value) => {
    switch (value) {
      case "en_attente": return "ti ti-hourglass";
      case "approuvee": return "ti ti-circle-check";
      case "rejetee": return "ti ti-circle-x";
      default: return "ti ti-infinity";
    }
  };

  const getPeriodeLabel = (periode) => {
    switch (periode) {
      case "matin": return "Matin";
      case "apres_midi": return "Après-midi";
      case "journee": return "Journée entière";
      default: return periode || "-";
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1).toString().padStart(2, "0")}/${date.getFullYear()}`;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1).toString().padStart(2, "0")}/${date.getFullYear()} à ${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
  };

  const isImageFile = (fileName) => {
    if (!fileName) return false;
    const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp"];
    return imageExtensions.some((ext) => fileName.toLowerCase().endsWith(ext));
  };

  const showImageDialogFunction = (imageUrl) => {
    setImageLoadError(false);
    setSelectedImage(getFullUrl(imageUrl));
    setImageZoom(1);
    setShowImageDialog(true);
  };

  const showDetailsDialog = (justification) => {
    setSelectedJustification(justification);
    setShowModal(true);
  };

  const filteredJustifications = filterStatut
    ? justifications.filter((j) => j.statut === filterStatut)
    : justifications;

  // ─── RENDU ───────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;700&family=Inter:wght@400;500;600;700&display=swap');

        *, *::before, *::after { box-sizing: border-box; }

        .justif-page {
          min-height: 100vh;
          background: var(--s2);
        }

        .justif-header {
          background: linear-gradient(135deg, var(--pr-dk), var(--pr));
          padding: 52px 16px 40px;
          text-align: center;
          border-radius: 0 0 40px 40px;
          position: relative;
          box-shadow: 0 4px 15px rgba(0,0,0,0.15);
        }

        .justif-header::after {
          content: "";
          position: absolute;
          left: 20%;
          right: 20%;
          bottom: 0;
          height: 3px;
          background: linear-gradient(90deg, transparent, var(--or), transparent);
        }

        .justif-header .back-btn {
          position: absolute;
          top: 44px;
          left: 16px;
          background: rgba(255,255,255,0.2);
          border: none;
          border-radius: 12px;
          padding: 8px;
          cursor: pointer;
          color: #fff;
          font-size: 24px;
          display: flex;
          width: 42px;
          height: 42px;
          align-items: center;
          justify-content: center;
        }

        .justif-header .back-btn:hover {
          background: rgba(255,255,255,0.3);
        }

        .justif-header .new-btn {
          position: absolute;
          top: 44px;
          right: 16px;
          background: rgba(255,255,255,0.2);
          border: none;
          border-radius: 12px;
          padding: 8px;
          cursor: pointer;
          color: #fff;
          font-size: 24px;
          display: flex;
          width: 42px;
          height: 42px;
          align-items: center;
          justify-content: center;
        }

        .justif-header .new-btn:hover {
          background: rgba(255,255,255,0.3);
        }

        .justif-header .header-icon {
          font-size: 60px;
          color: #fff;
          margin-bottom: 10px;
          display: block;
        }

        .justif-header h1 {
          font-family: 'Poppins', sans-serif;
          color: #fff;
          font-size: 24px;
          font-weight: 700;
          margin: 20px 0 0;
        }

        .justif-header p {
          font-family: 'Inter', sans-serif;
          color: rgba(255,255,255,0.7);
          font-size: 14px;
          margin: 8px 0 0;
        }

        .justif-eleve {
          margin: 16px;
          padding: 16px;
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .justif-eleve .avatar {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: var(--pr-bg);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          font-weight: 700;
          color: var(--pr);
          flex-shrink: 0;
        }

        .justif-eleve .info {
          flex: 1;
        }

        .justif-eleve .info .name {
          font-family: 'Poppins', sans-serif;
          font-size: 16px;
          font-weight: 700;
          color: var(--t);
          margin: 0 0 2px;
        }

        .justif-eleve .info .classe {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--m);
          margin: 0;
        }

        .justif-eleve .count {
          padding: 6px 12px;
          border-radius: 20px;
          background: var(--gn-bg);
          font-family: 'Inter', sans-serif;
          font-size: 12px;
          font-weight: 600;
          color: var(--gn);
        }

        .justif-eleve .count.empty {
          background: var(--s2);
          color: var(--h);
        }

        .justif-filters {
          display: flex;
          gap: 8px;
          margin: 8px 16px;
          overflow-x: auto;
          padding-bottom: 4px;
        }

        .justif-filters .filter-btn {
          padding: 6px 14px;
          border-radius: 30px;
          border: 1px solid rgba(27,79,128,0.25);
          background: #fff;
          cursor: pointer;
          flex-shrink: 0;
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          transition: all 0.2s;
          color: var(--pr);
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .justif-filters .filter-btn.active {
          background: var(--pr);
          color: #fff;
          font-weight: 600;
        }

        .justif-filters .filter-btn:hover:not(.active) {
          background: var(--pr-bg);
        }

        .justif-list {
          padding: 0 16px 16px;
        }

        .justif-card {
          margin-bottom: 16px;
          background: #fff;
          border-radius: 20px;
          box-shadow: 0 4px 10px rgba(0,0,0,0.06);
          cursor: pointer;
          transition: transform 0.2s, box-shadow 0.2s;
          overflow: hidden;
        }

        .justif-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.1);
        }

        .justif-card .content {
          padding: 16px;
        }

        .justif-card .header-row {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 12px;
        }

        .justif-card .header-row .icon-box {
          padding: 10px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .justif-card .header-row .info {
          flex: 1;
        }

        .justif-card .header-row .info .title {
          font-family: 'Poppins', sans-serif;
          font-size: 15px;
          font-weight: 600;
          color: var(--t);
          margin: 0 0 4px;
        }

        .justif-card .header-row .info .date {
          display: flex;
          align-items: center;
          gap: 4px;
          font-family: 'Inter', sans-serif;
          font-size: 12px;
          color: var(--m);
        }

        .justif-card .header-row .statut {
          padding: 5px 10px;
          border-radius: 20px;
          display: flex;
          align-items: center;
          gap: 4px;
          font-family: 'Inter', sans-serif;
          font-size: 11px;
          font-weight: 600;
        }

        .justif-card .description {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--m);
          margin: 0 0 8px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .justif-card .meta {
          display: flex;
          align-items: center;
          gap: 6px;
          font-family: 'Inter', sans-serif;
          font-size: 11px;
          color: var(--h);
        }

        .justif-card .comment {
          margin-top: 10px;
          padding: 8px 12px;
          background: #fff7ed;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 6px;
          font-family: 'Inter', sans-serif;
          font-size: 11px;
          color: #ea580c;
        }

        .justif-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          min-height: 250px;
        }

        .justif-empty i {
          font-size: 64px;
          color: var(--h);
          opacity: 0.5;
        }

        .justif-empty h3 {
          font-family: 'Inter', sans-serif;
          font-size: 18px;
          font-weight: 600;
          color: var(--t);
          margin: 16px 0 8px;
        }

        .justif-empty p {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--m);
          text-align: center;
          margin: 0 0 24px;
        }

        .justif-empty .btn-new {
          background: var(--pr);
          border: none;
          border-radius: 30px;
          padding: 12px 24px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          font-family: 'Inter', sans-serif;
          color: #fff;
          font-weight: 500;
          font-size: 14px;
        }

        .justif-empty .btn-new:hover {
          background: var(--pr-lt);
        }

        .justif-modal-overlay {
          position: fixed;
          inset: 0;
          z-index: 2000;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: flex-end;
          justify-content: center;
        }

        .justif-modal {
          background: #fff;
          border-radius: 25px 25px 0 0;
          width: 100%;
          max-width: 430px;
          max-height: 85vh;
          overflow-y: auto;
          padding: 20px 24px 28px;
        }

        .justif-modal .handle {
          width: 40px;
          height: 4px;
          background: var(--b);
          border-radius: 2px;
          margin: 0 auto 20px;
        }

        .justif-modal .modal-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px;
        }

        .justif-modal .modal-header .title {
          flex: 1;
          font-family: 'Poppins', sans-serif;
          font-size: 20px;
          font-weight: 700;
          color: var(--t);
          margin: 0;
        }

        .justif-modal .detail-item {
          display: flex;
          gap: 12px;
          margin-bottom: 16px;
        }

        .justif-modal .detail-item .icon {
          color: var(--pr);
          margin-top: 2px;
        }

        .justif-modal .detail-item .label {
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 12px;
          color: var(--m);
          margin-bottom: 2px;
        }

        .justif-modal .detail-item .value {
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          color: var(--t);
        }

        .justif-modal .detail-item .value.long {
          line-height: 1.4;
        }

        .justif-modal .detail-item .value.link {
          color: #2563eb;
          text-decoration: underline;
          cursor: pointer;
        }

        .justif-modal .image-preview {
          border-radius: 12px;
          overflow: hidden;
          cursor: pointer;
          margin-bottom: 4px;
        }

        .justif-modal .image-preview img {
          width: 100%;
          height: 150px;
          object-fit: cover;
        }

        .justif-modal .image-preview .fallback {
          height: 150px;
          background: var(--s2);
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
        }

        .justif-modal .image-preview .fallback div {
          text-align: center;
          color: var(--h);
        }

        .justif-modal .comment-box {
          padding: 12px;
          background: #fff7ed;
          border-radius: 12px;
          margin-top: 4px;
        }

        .justif-modal .comment-box .label {
          display: flex;
          align-items: center;
          gap: 6px;
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 13px;
          color: #ea580c;
          margin-bottom: 4px;
        }

        .justif-modal .comment-box .text {
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          color: var(--t);
          margin: 0;
        }

        .image-dialog {
          position: fixed;
          inset: 0;
          z-index: 3000;
          background: rgba(0,0,0,0.87);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }

        .image-dialog img {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
          transition: transform 0.15s ease;
          cursor: zoom-in;
        }

        .image-dialog .close-btn {
          position: absolute;
          top: 40px;
          right: 16px;
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: rgba(0,0,0,0.54);
          border: none;
          cursor: pointer;
          color: #fff;
          font-size: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .image-dialog .zoom-hint {
          position: absolute;
          bottom: 16px;
          left: 50%;
          transform: translateX(-50%);
          padding: 6px 12px;
          background: rgba(0,0,0,0.54);
          border-radius: 20px;
          color: rgba(255,255,255,0.7);
          font-family: 'Inter', sans-serif;
          font-size: 11px;
        }

        /* Carte d'erreur intégrée */
        .justif-error {
          background: #fff;
          border-radius: 20px;
          padding: 40px 20px;
          text-align: center;
          box-shadow: 0 4px 18px rgba(0,0,0,0.08);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          margin: 16px;
        }
        .justif-error i.alert-icon {
          font-size: 48px;
          color: var(--rd);
        }
        .justif-error h2 {
          font-family: 'Poppins', sans-serif;
          font-size: 17px; font-weight: 600;
          color: var(--t); margin: 4px 0 0;
        }
        .justif-error p {
          font-family: 'Inter', sans-serif;
          font-size: 13px; color: var(--m);
          margin: 0 0 12px;
        }
        .justif-error .actions-row {
          display: flex; gap: 8px;
          flex-wrap: wrap; justify-content: center;
        }
        .justif-error .btn {
          border: none; border-radius: 30px;
          padding: 10px 22px; cursor: pointer;
          font-family: 'Inter', sans-serif;
          font-size: 13px; font-weight: 600;
          color: #fff; background: var(--pr);
          display: inline-flex; align-items: center; gap: 6px;
          transition: opacity 0.2s;
        }
        .justif-error .btn:hover { opacity: 0.9; }
        .justif-error .btn.btn-secondary {
          background: var(--b2); color: var(--t);
        }

        /* Loader intégré */
        .justif-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 20px;
          gap: 14px;
        }
        .justif-loading .spinner {
          width: 44px;
          height: 44px;
          border: 4px solid var(--pr-bg, #e5eef7);
          border-top-color: var(--pr);
          border-radius: 50%;
          animation: justif-spin 0.8s linear infinite;
        }
        @keyframes justif-spin { to { transform: rotate(360deg); } }
        .justif-loading span {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--m);
        }

        @media (max-width: 480px) {
          .justif-header {
            padding: 44px 16px 32px;
          }
          .justif-header h1 {
            font-size: 20px;
          }
          .justif-modal {
            padding: 16px 18px 24px;
          }
        }
      `}</style>

      <div className="justif-page">
        {/* Header TOUJOURS affiché */}
        <div className="justif-header">
          <button className="back-btn" onClick={() => navigate(-1)} aria-label="Retour">
            <i className="ti ti-arrow-left" />
          </button>
          <button
            className="new-btn"
            onClick={() => navigate(`/${slug}/parent/nouvelle-justification/${enfantId}`)}
            aria-label="Nouvelle justification"
          >
            <i className="ti ti-plus" />
          </button>

          <i className="ti ti-file-text header-icon" />
          <h1>Justifications d'absence</h1>
          <p>Consultez et gérez les justifications</p>
        </div>

        {loading ? (
          /* ── ÉTAT CHARGEMENT (sous le header) ── */
          <div className="justif-loading">
            <div className="spinner" />
            <span>Chargement des justifications…</span>
          </div>
        ) : error ? (
          /* ── ÉTAT ERREUR (sous le header) ── */
          <div className="justif-error">
            <i className="ti ti-alert-circle alert-icon" />
            <h2>Erreur de chargement</h2>
            <p>{error}</p>
            <div className="actions-row">
              <button className="btn" onClick={loadData}>
                <i className="ti ti-refresh" />
                Réessayer
              </button>
              <button className="btn btn-secondary" onClick={() => navigate(-1)}>
                <i className="ti ti-arrow-left" />
                Retour
              </button>
            </div>
          </div>
        ) : (
          /* ── CONTENU ── */
          <>
            {/* Enfant */}
            {enfant && (
              <div className="justif-eleve">
                <div className="avatar">
                  {enfant.prenom?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="info">
                  <p className="name">{enfant.prenom} {enfant.nom}</p>
                  <p className="classe">Classe : {enfant.inscriptions?.[0]?.classe_nom || "-"}</p>
                </div>
                <div className={`count ${justifications.length === 0 ? "empty" : ""}`}>
                  {justifications.length} justif.
                </div>
              </div>
            )}

            {/* Filtres */}
            <div className="justif-filters">
              {[
                { value: "", label: "Tous" },
                { value: "en_attente", label: "En attente" },
                { value: "approuvee", label: "Approuvées" },
                { value: "rejetee", label: "Rejetées" },
              ].map((filter) => {
                const isActive = filterStatut === filter.value;
                return (
                  <button
                    key={filter.value}
                    className={`filter-btn ${isActive ? "active" : ""}`}
                    onClick={() => setFilterStatut(filter.value)}
                  >
                    <i className={getFilterIcon(filter.value)} style={{ fontSize: 14 }} />
                    {filter.label}
                  </button>
                );
              })}
            </div>

            {/* Liste */}
            <div className="justif-list">
              {filteredJustifications.length === 0 ? (
                <div className="justif-empty">
                  <i className="ti ti-file-text" />
                  <h3>Aucune justification</h3>
                  <p>Vous n'avez pas encore soumis de justification</p>
                  <button
                    className="btn-new"
                    onClick={() => navigate(`/${slug}/parent/nouvelle-justification/${enfantId}`)}
                  >
                    <i className="ti ti-plus" />
                    Nouvelle justification
                  </button>
                </div>
              ) : (
                filteredJustifications.map((j) => {
                  const statut = j.statut || "en_attente";

                  return (
                    <div key={j.id} className="justif-card" onClick={() => showDetailsDialog(j)}>
                      <div className="content">
                        <div className="header-row">
                          <div className="icon-box" style={{ background: getStatutBg(statut) }}>
                            <i className={getMotifIcon(j.motif)} style={{ color: getStatutColor(statut), fontSize: 20 }} />
                          </div>
                          <div className="info">
                            <p className="title">{getMotifLabel(j.motif)}</p>
                            <div className="date">
                              <i className="ti ti-calendar" style={{ fontSize: 11 }} />
                              {formatDate(j.date_absence)}
                            </div>
                          </div>
                          <div className="statut" style={{ background: getStatutBg(statut), color: getStatutColor(statut) }}>
                            <i className={getStatutIcon(statut)} style={{ fontSize: 12 }} />
                            {getStatutLabel(statut)}
                          </div>
                        </div>

                        <p className="description">{j.description || "Aucune description"}</p>

                        <div className="meta">
                          <i className="ti ti-clock" style={{ fontSize: 11 }} />
                          Soumis le {formatDateTime(j.date_soumission)}
                        </div>

                        {j.commentaire_admin && (
                          <div className="comment">
                            <i className="ti ti-message-circle" style={{ fontSize: 12 }} />
                            {j.commentaire_admin}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>

      {/* Modal Détails */}
      {showModal && selectedJustification && (
        <div className="justif-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="justif-modal" onClick={(e) => e.stopPropagation()}>
            <div className="handle" />

            <div className="modal-header">
              <i className={getMotifIcon(selectedJustification.motif)} style={{ fontSize: 28, color: "var(--pr)" }} />
              <h2 className="title">{getMotifLabel(selectedJustification.motif)}</h2>
              <div className="statut" style={{
                background: getStatutBg(selectedJustification.statut),
                color: getStatutColor(selectedJustification.statut),
                padding: "6px 12px",
                borderRadius: 20,
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: 12,
                fontWeight: 600,
              }}>
                {getStatutLabel(selectedJustification.statut)}
              </div>
            </div>

            <div className="detail-item">
              <div className="icon"><i className="ti ti-calendar" style={{ fontSize: 18 }} /></div>
              <div>
                <div className="label">Date d'absence</div>
                <div className="value">{formatDate(selectedJustification.date_absence)}</div>
              </div>
            </div>

            <div className="detail-item">
              <div className="icon"><i className="ti ti-clock" style={{ fontSize: 18 }} /></div>
              <div>
                <div className="label">Période</div>
                <div className="value">{getPeriodeLabel(selectedJustification.periode)}</div>
              </div>
            </div>

            <div className="detail-item">
              <div className="icon"><i className="ti ti-file-description" style={{ fontSize: 18 }} /></div>
              <div>
                <div className="label">Description</div>
                <div className="value long">{selectedJustification.description || "Aucune description"}</div>
              </div>
            </div>

            {selectedJustification.piece_jointe && (
              isImageFile(selectedJustification.piece_jointe) ? (
                <div className="detail-item" style={{ flexDirection: "column" }}>
                  <div style={{ display: "flex", gap: 12, marginBottom: 8 }}>
                    <div className="icon"><i className="ti ti-photo" style={{ fontSize: 18 }} /></div>
                    <div className="label" style={{ marginBottom: 0 }}>Pièce jointe</div>
                  </div>
                  <div className="image-preview" onClick={() => showImageDialogFunction(selectedJustification.piece_jointe)}>
                    {imageLoadError ? (
                      <div className="fallback">
                        <div>Image non disponible</div>
                      </div>
                    ) : (
                      <img
                        src={getFullUrl(selectedJustification.piece_jointe)}
                        alt="Pièce jointe"
                        onError={() => setImageLoadError(true)}
                      />
                    )}
                  </div>
                </div>
              ) : (
                <div className="detail-item">
                  <div className="icon"><i className="ti ti-paperclip" style={{ fontSize: 18 }} /></div>
                  <div>
                    <div className="label">Pièce jointe</div>
                    <div
                      className="value link"
                      onClick={() => showImageDialogFunction(selectedJustification.piece_jointe)}
                    >
                      Voir le document
                    </div>
                  </div>
                </div>
              )
            )}

            {selectedJustification.commentaire_admin && (
              <div className="comment-box">
                <div className="label">
                  <i className="ti ti-shield-check" />
                  Commentaire administration
                </div>
                <p className="text">{selectedJustification.commentaire_admin}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Dialog Image plein écran avec zoom */}
      {showImageDialog && (
        <div className="image-dialog" onClick={() => setShowImageDialog(false)}>
          <img
            src={selectedImage}
            alt="Plein écran"
            style={{ transform: `scale(${imageZoom})` }}
            onClick={(e) => {
              e.stopPropagation();
              setImageZoom((z) => (z >= 3 ? 1 : z + 1));
            }}
          />
          <button className="close-btn" onClick={() => setShowImageDialog(false)}>
            <i className="ti ti-x" />
          </button>
          <div className="zoom-hint">Cliquez sur l'image pour zoomer</div>
        </div>
      )}
    </>
  );
}